import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import ComicTable from './ComicTable';
import ComicForm from './ComicForm';
import { getComics, createComic, updateComic, deleteComic } from '../services/comicsApi';
import { Comic } from '../types/Comic';
import './ComicsList.css';

const ComicsList: React.FC = () => {
  const [comics, setComics] = useState<Comic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingComic, setEditingComic] = useState<Comic | null>(null);
  const [filterGender, setFilterGender] = useState<string>('all');
  const [filterFriend, setFilterFriend] = useState<string>('all');

  useEffect(() => {
    loadComics();
  }, []);

  const loadComics = async () => {
    try {
      setLoading(true);
      setError(null);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString() || '';
      const data = await getComics(token);
      setComics(data);
    } catch (err: any) {
      console.error('Error loading comics:', err);
      setError(err.message || 'Failed to load comics');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateComic = async (comic: Partial<Comic>) => {
    try {
      setError(null);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString() || '';
      await createComic(token, comic as Omit<Comic, never>);
      await loadComics();
      setShowForm(false);
    } catch (err: any) {
      console.error('Error creating comic:', err);
      setError(err.message || 'Failed to create comic');
      throw err;
    }
  };

  const handleUpdateComic = async (email: string, updates: Partial<Comic>) => {
    try {
      setError(null);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString() || '';
      await updateComic(token, email, updates);
      await loadComics();
      setEditingComic(null);
    } catch (err: any) {
      console.error('Error updating comic:', err);
      setError(err.message || 'Failed to update comic');
      throw err;
    }
  };

  const handleDeleteComic = async (email: string) => {
    if (!window.confirm('Are you sure you want to delete this comic?')) {
      return;
    }

    try {
      setError(null);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString() || '';
      await deleteComic(token, email);
      await loadComics();
    } catch (err: any) {
      console.error('Error deleting comic:', err);
      setError(err.message || 'Failed to delete comic');
    }
  };

  const handleEdit = (comic: Comic) => {
    setEditingComic(comic);
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingComic(null);
  };

  const filteredComics = comics.filter(comic => {
    if (filterGender !== 'all' && comic.gender !== filterGender) {
      return false;
    }
    if (filterFriend === 'friends' && !comic.friend) {
      return false;
    }
    if (filterFriend === 'non-friends' && comic.friend) {
      return false;
    }
    return true;
  });

  return (
    <div className="comics-list">
      <div className="comics-header">
        <h2>Comics Management</h2>
        <button
          onClick={() => setShowForm(true)}
          className="add-button"
          disabled={showForm}
        >
          + Add Comic
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="close-error">×</button>
        </div>
      )}

      {showForm && (
        <ComicForm
          comic={editingComic}
          onSubmit={editingComic ?
            (updates) => handleUpdateComic(editingComic.email, updates) :
            handleCreateComic
          }
          onCancel={handleCancelForm}
        />
      )}

      <div className="filters">
        <div className="filter-group">
          <label htmlFor="gender-filter">Gender:</label>
          <select
            id="gender-filter"
            value={filterGender}
            onChange={(e) => setFilterGender(e.target.value)}
          >
            <option value="all">All</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="friend-filter">Friend Status:</label>
          <select
            id="friend-filter"
            value={filterFriend}
            onChange={(e) => setFilterFriend(e.target.value)}
          >
            <option value="all">All</option>
            <option value="friends">Friends Only</option>
            <option value="non-friends">Non-Friends Only</option>
          </select>
        </div>

        <div className="stats">
          <span>Showing {filteredComics.length} of {comics.length} comics</span>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading comics...</div>
      ) : (
        <ComicTable
          comics={filteredComics}
          onEdit={handleEdit}
          onDelete={handleDeleteComic}
        />
      )}
    </div>
  );
};

export default ComicsList;
